import React, { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { X, Sparkles, ScanLine, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const SAMPLES = [
  {
    label: "Customer email — Q4 invoice",
    text: `From: Marcus Chen <marcus@northwind.com>
Subject: Re: Q4 engagement — invoice status

Hi — thanks for the reminder. I checked with Finance and they will process the $4,800 invoice for the Q4 engagement by Friday. We're also planning to renew for Q1 — I'll send over a signed order form early next week.

Also, can you share the customer reference deck? Would love to show it to a peer of mine.

Best,
Marcus`,
  },
  {
    label: "Meeting transcript — Partnership call",
    text: `[Emily] Great — so on our side, I'll get you the revised proposal with the updated pricing tier by Friday.
[You] Perfect. And I'll introduce you to Sarah at Meridian tomorrow — she runs partnerships there.
[Emily] Amazing. We might also be able to do a joint webinar in Q2, but let's talk about that later.
[You] Sounds good. I'll draft a one-pager on the co-marketing structure and send it over next week.
[Emily] Also, if legal approves the MSA, I'll sign it Friday.`,
  },
  {
    label: "Payment terms with split",
    text: `Thanks for putting the SOW together. We'll wire the $12,000 retainer once the SOW is countersigned. Half now and the remaining $5,000 will follow on the 30th once we cross the first milestone.

Excited to kick off.
— Elena`,
  },
];

const STAGES = [
  "Reading conversation",
  "Separating promises from intentions",
  "Resolving people and deadlines",
  "Checking for existing commitments",
  "Preparing review",
];

export const PasteDialog = ({ open, onOpenChange, onAnalyzed }) => {
  const [text, setText] = useState("");
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState(0);
  const [error, setError] = useState(null);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!loading) return;
    setStage(0);
    const iv = setInterval(() => {
      setStage((s) => Math.min(s + 1, STAGES.length - 1));
    }, 1400);
    return () => clearInterval(iv);
  }, [loading]);

  const submit = async () => {
    if (submittingRef.current) return;
    if (text.trim().length < 20) {
      toast.error("Paste at least a few sentences of conversation.");
      return;
    }
    submittingRef.current = true;
    setError(null);
    setLoading(true);
    try {
      const res = await api.analyze({
        content: text,
        title: title || undefined,
        source_type: "paste",
        user_name: "You",
      });
      const n = (res.detected || []).length;
      const ff = (res.fulfillment_matches || []).length;
      const rej = (res.rejected || []).length;
      if (n === 0 && ff === 0) {
        toast(
          rej > 0
            ? `No firm commitments — ${rej} soft/intent statements ignored.`
            : "No firm commitments found."
        );
      } else if (ff > 0) {
        toast.success(`Detected ${n} · ${ff} possible fulfilment — review below.`);
      } else {
        toast.success(`Detected ${n} commitment${n === 1 ? "" : "s"} — review below.`);
      }
      setText("");
      setTitle("");
      onOpenChange(false);
      onAnalyzed?.(res);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Failed to analyze";
      setError(String(msg));
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="paste-dialog"
        className="max-w-3xl bg-ledger-surface border border-ledger-border p-0 gap-0"
      >
        <DialogHeader className="px-8 pt-8 pb-4 border-b border-ledger-border">
          <DialogTitle className="font-display text-3xl text-ledger-ink">
            What commitments exist in this conversation?
          </DialogTitle>
          <DialogDescription className="text-sm text-ledger-sub">
            Paste an email thread, meeting transcript, chat, or call notes. We&rsquo;ll surface
            genuine commitments &mdash; not intentions, possibilities, or requests. You review
            before anything enters the ledger.
          </DialogDescription>
        </DialogHeader>

        <div className="px-8 py-6 space-y-4">
          <input
            data-testid="paste-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Optional title — e.g. Q4 renewal thread with Northwind"
            className="w-full bg-white border border-ledger-border rounded-sm px-3 py-2 font-sans text-sm placeholder:text-ledger-muted focus:outline-none focus:ring-2 focus:ring-ledger-ink"
          />

          <div className={"relative " + (loading ? "scanning rounded-sm" : "")}>
            <textarea
              data-testid="paste-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Paste your conversation here…"
              rows={12}
              disabled={loading}
              className="w-full resize-y bg-white border border-dashed border-ledger-border rounded-sm p-5 font-mono text-[13px] leading-6 text-ledger-ink placeholder:text-ledger-muted placeholder:font-serif placeholder:italic focus:outline-none focus:ring-2 focus:ring-ledger-ink disabled:opacity-70"
            />
            {loading && (
              <div className="absolute inset-0 grid place-items-center pointer-events-none">
                <div
                  data-testid="paste-progress"
                  className="flex items-center gap-2 font-mono text-xs text-[#78350F] bg-white border border-ledger-goldBorder px-3 py-1.5 rounded-sm"
                >
                  <ScanLine className="h-4 w-4 animate-pulse" />
                  {STAGES[stage]}…
                </div>
              </div>
            )}
          </div>

          {error && (
            <div
              data-testid="paste-error"
              className="bg-[#FEE2E2] border border-[#EF4444] rounded-sm p-3 text-sm text-[#7F1D1D] flex items-start justify-between gap-3"
            >
              <div>
                <div className="font-semibold">Analysis failed</div>
                <div className="text-[12px] mt-0.5">{error}</div>
                <div className="text-[11px] font-mono text-ledger-muted mt-1">
                  Your conversation is still here — try again.
                </div>
              </div>
              <button
                onClick={submit}
                data-testid="paste-retry"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-ledger-ink text-white text-xs font-medium hover:bg-neutral-800"
              >
                <RefreshCw className="h-3.5 w-3.5" /> Retry
              </button>
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] uppercase tracking-[0.18em] text-ledger-muted">
              Try a sample:
            </span>
            {SAMPLES.map((s, i) => (
              <button
                key={i}
                data-testid={`sample-${i}`}
                onClick={() => setText(s.text)}
                disabled={loading}
                className="font-mono text-[11px] px-2 py-1 rounded-sm border border-ledger-border text-ledger-sub hover:border-ledger-ink hover:text-ledger-ink transition-colors duration-150"
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="px-8 py-5 border-t border-ledger-border flex items-center justify-between">
          <button
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 text-sm text-ledger-sub hover:text-ledger-ink transition-colors duration-150 inline-flex items-center gap-1"
            data-testid="paste-cancel"
          >
            <X className="h-4 w-4" /> Cancel
          </button>
          <button
            onClick={submit}
            disabled={loading}
            data-testid="paste-submit"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ledger-ink text-white text-sm font-medium hover:bg-neutral-800 disabled:opacity-60 transition-colors duration-200"
          >
            <Sparkles className="h-4 w-4" />
            {loading ? "Reading…" : "Detect commitments"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
